import {
  Box,
  Container,
  createStyles,
  CssBaseline,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import firebase from 'firebase/app'
import React, { useContext, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import PhotoCamera from '@material-ui/icons/PhotoCamera'
import { CustomDialogContent, Loading } from '..'
import { openSnackbar } from '../../ducks/snackbar/actions'

import { addCircle, addFileToFirebaseStorage } from 'daily-dose-common'

import { FirestoreContext } from '../../providers/firestore-provider'

import { asyncError } from '../../ducks/ui/actions'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      display: 'none',
    },
    large: {
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
    image: {
      maxWidth: '100%',
      height: '200px',
    },
    typography: {
      align: 'center',
    },
    center: {
      alignSelf: 'center',
    },
    column: {
      flexDirection: 'column',
    },
  })
)

type Inputs = {
  name: string
  description: string
}

interface IAddCircleProps {
  circlesRef: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>
  onAddCircle: () => void
  onCancelCircle: () => void
}

const AddCircleForm = ({
  circlesRef,
  onAddCircle,
  onCancelCircle,
}: IAddCircleProps) => {
  const classes = useStyles()
  const { currentUser } = useContext(FirestoreContext)
  const { handleSubmit, errors, control } = useForm<Inputs>()

  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const [file, setFile] = useState(({} as any) || null)
  const [imageUrl, setImageUrl] = useState('' as any)

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.readAsDataURL(file) as any

      reader.onloadend = function () {
        // Picture is smaller than 8mb
        if (file.size < 10 * 1024 * 1024) {
          setFile(file)
          setImageUrl(reader.result)
        } else {
          alert('Choose a picture with less than 10MB size')
        }
      }.bind(this)
    }
  }

  const onSubmit = async (data: Inputs) => {
    const { name, description } = data

    if (loading) return null

    // TODO: We might not ned this id since we're already chekcing currentUser
    const { id } = currentUser || {}
    const isUser = !!(id && currentUser)
    let photoURL = null
    if (isUser) {
      try {
        setLoading(true)
        // TODO: Add loading when we move dialog to redux
        if (file?.name) {
          photoURL = await addFileToFirebaseStorage('circle-profiles', id!, file)
        }

        await addCircle(
          {
            name,
            description,
            photoURL,
          },
          currentUser!,
          circlesRef
        )
        dispatch(
          openSnackbar(
            `Awesome! You just created ${name} group. Now let's add some doses.`,
            'success'
          )
        )
        setLoading(false)
        onAddCircle()
      } catch (error) {
        console.warn('Error putting photo in bucket ', error)
        setLoading(false)
        dispatch(asyncError(error))
        dispatch(openSnackbar('Opps. Something happened. Please try again', 'error'))
      }
    } else {
      dispatch(openSnackbar('Please add both a name and description', 'info'))
    }
  }

  return (
    <CustomDialogContent
      title="Add Group"
      confirmText="Add"
      onCancel={onCancelCircle}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        {loading && <Loading />}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="AddCircle"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <input
            accept="image/*"
            className={classes.input}
            id="icon-button-file"
            type="file"
            onChange={onChangeFile}
          />
          <label htmlFor="icon-button-file" className={classes.center}>
            <Box alignSelf="center">
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
              >
                <div className={classes.column}>
                  {imageUrl ? (
                    <img src={imageUrl} className={classes.image} />
                  ) : (
                    <PhotoCamera className={classes.large} />
                  )}
                  <Typography
                    variant="body2"
                    color="inherit"
                    className={classes.typography}
                  >
                    {imageUrl ? 'Change picture ' : 'Upload picture'}
                  </Typography>
                </div>
              </IconButton>
            </Box>
          </label>

          <Controller
            as={TextField}
            name="name"
            control={control}
            rules={{ required: true }}
            defaultValue=""
            variant="outlined"
            margin="normal"
            fullWidth
            label="Name"
            autoComplete="off"
            type="text"
            placeholder="Because even the Beatles had one..."
            error={!!errors.name}
            helperText={!!errors.name && 'The name of your group is missing.'}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Controller
            as={TextField}
            multiline
            name="description"
            control={control}
            rules={{ required: false }}
            defaultValue=""
            variant="outlined"
            margin="normal"
            fullWidth
            label="Description"
            autoComplete="off"
            type="text"
            placeholder="Because why not?"
            rows={4}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </form>
      </Container>
    </CustomDialogContent>
  )
}

export default AddCircleForm
